import _ from 'lodash'
import type { Update, Transform, Materializer } from '@wix/thunderbolt-types'
import { createRef } from '@wix/materializer'
import { DataMapName, Component, pointers, Style, RefSchemas, Language } from '@wix/thunderbolt-becky-types'
import type { Batched, UpdateData, UpdateStructure } from '@wix/thunderbolt-catharsis'
import { removeHash, createRefResolver } from '@wix/thunderbolt-catharsis'
import type {
	ByRefsisOps,
	ByRefsisTransactionWithInvalidations,
	RawModel,
	WithNamespace,
	UpdateTranslation,
	UpdateEnvironment,
	updateEntireStructure,
} from '../types'
import {
	getRefCreatorInNamespace,
	isInflatedId,
	getLeafsOverridesMapAspectName,
	getQueryNameFromLeafsOverridesMapAspectName,
	getContextAndTemplateCompIdFromInflatedDataId,
	isSystemStyle,
	markRemoteOverride,
	isRefComponent,
} from './byRefUtils'
import {
	SITE_NAMESPACE,
	EMPTY_SCHEMA,
	DATA_MAP_NAMES,
	DATA_MAP_TO_QUERY,
	OVERRIDES_MAP_DELIMITER,
	QUERY_MAPPING,
	DATA_QUERY_TO_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RAW_TRANSLATIONS_ASPECT,
	RESOLVED_TRANSLATIONS_ASPECT,
	ORIGINAL_LANGUAGE_REF,
	LANGUAGE_REF,
	STRUCTURE_ASPECT,
	ROOT_COMP_ID_ASPECT,
	SYSTEM_STYLES,
	INVALIDATIONS_NAMESPACE,
	RESOLVED_REFS_NAMESPACE,
	HAS_SITE_OVERRIDE,
	INVALIDATE_STRUCTURE,
	RESOLVED_REF_COMP,
	DELETED_REF_COMPS_ASPECT,
} from './constants'
import { templateCompNodes, templateDataNodes } from './templateNodes'
import { defaultDataNodeToViewItem } from './templateNodes/dataNodes/utils/dataNodeUtils'

const DATA_ITEM_TYPES_TO_RESOLVE = new Set<string>([
	'RefArray',
	'VariantRelation',
	'Reactions',
	'Pattern',
	'TimeAnimationOptions',
	'ScrubAnimationOptions',
	'CanvasAnimationOptions',
	'InnerElement',
])

const {
	data: {
		DATA_MAPS: { STYLE, VARIANTS },
	},
	components: {
		PROPERTY_TYPES: { DATA_QUERY, DESIGN_QUERY },
	},
} = pointers

const VARIANTS_PER_PAGE = 'variants_per_page'
const excludedPathsByDataItemType = { VariantRelation: ['from', 'remoteVariants'] }

export const getModelUpdateTransformer = (rawModel: RawModel): Transform => (value, path) => {
	const [namespace, aspect, id] = path

	if (
		namespace === ENVIRONMENT_NAMESPACE ||
		aspect.endsWith('LeafsOverridesMap') ||
		aspect === DATA_QUERY_TO_COMP_ID_ASPECT ||
		aspect === INVALIDATIONS_NAMESPACE
	) {
		return value
	}

	if (aspect === RESOLVED_TRANSLATIONS_ASPECT) {
		if (!value) {
			return
		}

		const { originalLanguage } = value
		let { language } = value

		const shouldTranslatePageData = language !== originalLanguage

		// Blocks doesn't support languages as Locales (<language>-<region>)
		if (namespace !== SITE_NAMESPACE) {
			language = language.split('-')[0] as Language
		}

		const shouldTranslateWidgetData = namespace !== SITE_NAMESPACE && language !== 'en'
		if (!shouldTranslatePageData && !shouldTranslateWidgetData) {
			return
		}

		return _.get(rawModel, [namespace, RAW_TRANSLATIONS_ASPECT, language, id])
	}

	if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
		const rawItem = _.get(rawModel, path)
		if (typeof rawItem === 'undefined') {
			return rawItem
		}

		if (templateDataNodes[aspect as DataMapName]?.toViewItem) {
			return templateDataNodes[aspect as DataMapName]!.toViewItem!({ rawItem, value })
		}

		return defaultDataNodeToViewItem(rawItem, value)
	}

	// namespace is either 'site' or a templateKey
	const component = _.get(rawModel, [namespace, STRUCTURE_ASPECT, id])
	if (component && templateCompNodes[aspect]) {
		return templateCompNodes[aspect].toViewItem(component, value)
	}

	return value
}

export const updateModel: ByRefsisTransactionWithInvalidations = (
	materializer: Materializer,
	rawModel: RawModel,
	modelUpdateFn: (ops: ByRefsisOps) => void,
	schemas: RefSchemas
) => {
	const getRefsForDataItem = createRefResolver(schemas, excludedPathsByDataItemType)
	const leafsOverridesToUpdate = new Map<string, Record<string, true>>()

	const updateDataItemIdToCompId = (
		batchUpdate: Update<void>,
		namespace: string,
		component: Component | undefined
	) => {
		if (!component) {
			return
		}

		const compId = component.id
		;[DATA_QUERY, DESIGN_QUERY].forEach((queryName) => {
			const dataQuery = component[queryName]
			if (dataQuery) {
				const dataQueryWithoutHash = removeHash(dataQuery)
				_.set(rawModel, [namespace, DATA_QUERY_TO_COMP_ID_ASPECT, dataQueryWithoutHash], compId)
				batchUpdate(namespace, DATA_QUERY_TO_COMP_ID_ASPECT, dataQueryWithoutHash, compId, EMPTY_SCHEMA)
			}
		})
	}

	const updateDataByNamespace: Batched<WithNamespace<UpdateData>> = (batchUpdate) => (
		namespace,
		dataMapName,
		id,
		dataItem
	) => {
		if (dataMapName === VARIANTS) {
			const pageId = _.get(dataItem, [`metaData`, `pageId`])
			if (pageId) {
				// when in blocks editor we need to know which variants belongs to which page,
				// so we won't have duplicate values in reactions
				_.setWith(rawModel, [VARIANTS_PER_PAGE, pageId, id], dataItem, Object)
			}
		}
		_.setWith(rawModel, [namespace, dataMapName, id], dataItem, Object)
		if (!dataItem) {
			batchUpdate(namespace, dataMapName, id)
			return
		}

		const createRefInTemplateNamespace = getRefCreatorInNamespace(namespace)
		const customDeps = templateDataNodes[dataMapName]?.getDependencies(dataItem, createRefInTemplateNamespace)
		const schemaRefs =
			dataItem.type?.startsWith('platform.builder') || DATA_ITEM_TYPES_TO_RESOLVE.has(dataItem.type)
				? getRefsForDataItem(dataItem, dataMapName, namespace)
				: null
		const dependencies = schemaRefs || customDeps ? { schema: schemaRefs, custom: customDeps } : null
		batchUpdate(namespace, dataMapName, id, dependencies, dependencies)

		// leaf override
		if (isInflatedId(id)) {
			const { templateCompId, context, inflatedCompId } = getContextAndTemplateCompIdFromInflatedDataId(
				id,
				dataMapName
			)
			const queryName = DATA_MAP_TO_QUERY[dataMapName]
			if (namespace !== SITE_NAMESPACE) {
				// Mark only remote overrides.
				// inflatedCompId in this case will not contain the outer most RefComponent
				// because outermost ref component is in the site namespace.
				markRemoteOverride(rawModel, queryName, inflatedCompId, true)
			} else {
				_.set(rawModel, [HAS_SITE_OVERRIDE, dataMapName, inflatedCompId], true)
			}
			const overridesAspect = getLeafsOverridesMapAspectName(context, queryName)

			const overridesMapPath = [namespace, overridesAspect, templateCompId]
			const overridesMapStringPath = overridesMapPath.join(OVERRIDES_MAP_DELIMITER)

			if (!leafsOverridesToUpdate.has(overridesMapStringPath)) {
				const overridesMap = { ..._.get(rawModel, overridesMapPath) }
				leafsOverridesToUpdate.set(overridesMapStringPath, overridesMap)
			}
			leafsOverridesToUpdate.get(overridesMapStringPath)![id] = true
		}
	}

	const updateData: Batched<UpdateData> = (batchUpdate) => (dataMapName, id, dataItem) => {
		if (dataMapName === STYLE && dataItem && isSystemStyle((dataItem as unknown) as Style)) {
			_.set(rawModel, [SITE_NAMESPACE, SYSTEM_STYLES, id], true)
		}

		updateDataByNamespace(batchUpdate)(SITE_NAMESPACE, dataMapName, id, dataItem)
	}

	const updateTranslationByNamespace: Batched<WithNamespace<UpdateTranslation>> = (batchUpdate) => (
		namespace,
		language,
		documentDataId,
		documentDataItem
	) => {
		_.setWith(rawModel, [namespace, RAW_TRANSLATIONS_ASPECT, language, documentDataId], documentDataItem, Object)

		if (!documentDataItem) {
			batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId)
			return
		}

		const dependencies = {
			originalLanguage: ORIGINAL_LANGUAGE_REF,
			language: LANGUAGE_REF,
		}

		batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId, dependencies, dependencies)
	}

	const updateTranslation: Batched<UpdateTranslation> = (batchUpdate) => (language, id, dataItem) =>
		updateTranslationByNamespace(batchUpdate)(SITE_NAMESPACE, language, id, dataItem)

	// update component with resolved children and update it in rawModel and materializer
	const updateResolvedRefComponentStructure = (
		batchUpdate: Update<void>,
		id: string,
		resolvedRefComp: Component,
		updatedRefComp: Component
	) => {
		updatedRefComp.components = resolvedRefComp.components
		_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, id], updatedRefComp)
		batchUpdate(RESOLVED_REFS_NAMESPACE, RESOLVED_REF_COMP, id, updatedRefComp, EMPTY_SCHEMA)
	}

	const updateStructureByNamespace: Batched<WithNamespace<UpdateStructure>> = (batchUpdate) => (
		namespace,
		id,
		component
	) => {
		const prevValue = _.get(rawModel, [namespace, STRUCTURE_ASPECT, id])
		_.setWith(rawModel, [namespace, STRUCTURE_ASPECT, id], component, Object)

		updateDataItemIdToCompId(batchUpdate, namespace, component)
		batchUpdate(namespace, STRUCTURE_ASPECT, id, component, EMPTY_SCHEMA)

		const isRefComp = isRefComponent(component || prevValue)

		// we need to clean up resolved structure upon parent ref comp deletion
		// only site refComponents can be deleted (wiw can not be deleted in consumption - it can be a ghost component)
		if (isRefComp && namespace === SITE_NAMESPACE) {
			if (!component) {
				batchUpdate(SITE_NAMESPACE, DELETED_REF_COMPS_ASPECT, id, true, EMPTY_SCHEMA)
			} else if (!prevValue) {
				batchUpdate(SITE_NAMESPACE, DELETED_REF_COMPS_ASPECT, id, false, EMPTY_SCHEMA)
			}
		}

		// if refComponent structure is updated, but the dataQuery is the same, we don't want to trigger re consumption
		if (component && isRefComp && prevValue?.dataQuery === component.dataQuery) {
			const resolvedRefComp = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, id])
			if (resolvedRefComp) {
				return updateResolvedRefComponentStructure(batchUpdate, id, resolvedRefComp, component)
			}
		}

		for (const structureCalculator in templateCompNodes) {
			if (!component) {
				batchUpdate(namespace, structureCalculator, id)
				continue
			}

			const dependencies = templateCompNodes[structureCalculator].getDependencies(
				component.id,
				component,
				getRefCreatorInNamespace(namespace)
			)

			if (!dependencies) {
				continue
			}

			batchUpdate(namespace, structureCalculator, id, dependencies, dependencies)
		}
	}

	const updateEntireStructure: Batched<updateEntireStructure> = (batchUpdate) => (structure) => {
		const prevStructure = _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT])
		_.forEach(prevStructure, (prevValue, id) => {
			const component = structure[id] ? structure[id] : undefined
			// if new value is not undefined or prev value exists, update
			if (component || prevValue) {
				updateSiteStructure(batchUpdate)(id, component)
			}
		})

		const newComponents = prevStructure ? _.pickBy(structure, (__, id) => !prevStructure[id]) : structure
		_.forEach(newComponents, (component) => {
			updateSiteStructure(batchUpdate)(component.id, component)
		})
	}

	const updateWidgetRootCompId = (templateKey: string, rootCompId: string) => {
		_.set(rawModel, [templateKey, ROOT_COMP_ID_ASPECT], rootCompId)
	}

	const updateSiteStructure: Batched<UpdateStructure> = (batchUpdate) => (id, component) =>
		updateStructureByNamespace(batchUpdate)(SITE_NAMESPACE, id, component)

	const invalidateResolvedStructure: Batched<UpdateStructure> = (batchUpdate) => (inflatedCompId, structure) => {
		_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedCompId], structure)
		// structure consumption node creates a ref to this path, in order to update the structure with the new query
		batchUpdate(RESOLVED_REFS_NAMESPACE, INVALIDATE_STRUCTURE, inflatedCompId, true)
	}

	const updateEnvironment: Batched<UpdateEnvironment> = (batchUpdate) => (environment: Record<string, any>) => {
		for (const envType in environment) {
			for (const key in environment[envType]) {
				batchUpdate(ENVIRONMENT_NAMESPACE, envType, key, environment[envType][key], EMPTY_SCHEMA)
			}
		}
	}

	const invalidateWidget: Batched<ByRefsisOps['invalidateWidget']> = (batchUpdate) => (templateKey) => {
		batchUpdate(
			INVALIDATIONS_NAMESPACE,
			INVALIDATIONS_NAMESPACE,
			templateKey,
			_.uniqueId('invalidation_'),
			EMPTY_SCHEMA
		)
	}

	const flushLeafsOverridesMapsUpdates = (batchUpdate: Update<void>) => {
		for (const [overridesMapStringPath, overridesMap] of leafsOverridesToUpdate) {
			const overridesMapPath = overridesMapStringPath.split(OVERRIDES_MAP_DELIMITER)
			const [overridesNamespace, overridesAspect, overridesCompId] = overridesMapPath
			const queryName = getQueryNameFromLeafsOverridesMapAspectName(overridesMapPath[1])
			_.set(rawModel, overridesMapPath, overridesMap)

			const overrideRefsMap = _.mapValues(overridesMap, (__, overrideId) =>
				createRef([overridesNamespace, QUERY_MAPPING[queryName], overrideId])
			)
			batchUpdate(overridesNamespace, overridesAspect, overridesCompId, overrideRefsMap, overrideRefsMap)
		}

		leafsOverridesToUpdate.clear()
	}

	return materializer.batch((batchUpdate) => {
		modelUpdateFn({
			updateData: updateData(batchUpdate),
			updateStructure: updateSiteStructure(batchUpdate),
			updateTranslation: updateTranslation(batchUpdate),
			updateWidgetRootCompId,
			updateWidgetData: updateDataByNamespace(batchUpdate),
			updateWidgetStructure: updateStructureByNamespace(batchUpdate),
			updateWidgetTranslation: updateTranslationByNamespace(batchUpdate),
			updateEnvironment: updateEnvironment(batchUpdate),
			invalidateWidget: invalidateWidget(batchUpdate),
			invalidateResolvedStructure: invalidateResolvedStructure(batchUpdate),
			updateEntireStructure: updateEntireStructure(batchUpdate),
		})

		flushLeafsOverridesMapsUpdates(batchUpdate)
	})
}
